// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Portal from "@material-ui/core/Portal";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "../Alert";
import { isAccessGranted } from "../utils/utils";
import { Privileges } from "../../Config";
import ApproveEngagementView from "./ApproveEngagementView";

const useStyles = makeStyles((theme) => ({
  page: {
    padding: 20,
  },
  createNewButton: {
    paddingRight: 8,
    paddingLeft: 8,
  },
  tabs: {
    "& .MuiTabs-indicator": {
      backgroundColor: "#ff7300",
    },
  },
  tab: {
    textTransform: "uppercase",
    color: "#ff7300",
    "&.Mui-selected": {
      color: "#ff7300",
      borderBottom: `3px solid ${theme.palette.primary.main}`,
    },
  },
}));

const tabProps = (index) => {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tab-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
};

const ApproveEngagements = ({ isInternal }) => {
  const classes = useStyles();
  const history = useHistory();

  const [tab, setTab] = useState(
    history && history.location && history.location.state
      ? history.location.state.tab
      : 0
  );

  const [tabIndexes, setTabIndexes] = useState({
    InternalEngagementsTab: 0,
    marketingEventsTab: 1,
  });

  const [isMessageBarOpen, setIsMessageBarOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertMessageType, setAlertMessageType] = useState("error");

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  useEffect(() => {
    let initIndex = 0;
    let InternalEngagementsTabIndex = -1;
    let marketingEventsTabIndex = -1;

    if (
      isAccessGranted([
        Privileges.APPROVE_INTERNAL_ENGAGEMENTS_APPROVE,
        Privileges.APPROVE_INTERNAL_ENGAGEMENTS_VIEW,
        Privileges.APPROVE_INTERNAL_ENGAGEMENTS_EDIT,
      ])
    ) {
      InternalEngagementsTabIndex = initIndex;
      initIndex++;
    }

    if (
      isAccessGranted([
        Privileges.APPROVE_MARKETING_EVENTS_APPROVE,
        Privileges.APPROVE_MARKETING_EVENTS_VIEW,
        Privileges.APPROVE_MARKETING_EVENTS_EDIT,
      ])
    ) {
      marketingEventsTabIndex = initIndex;
      initIndex++;
    }

    setTabIndexes({
      InternalEngagementsTab: InternalEngagementsTabIndex,
      marketingEventsTab: marketingEventsTabIndex,
    });
  }, []);

  const setMessageBar = (message, isOpen, type) => {
    setAlertMessage(message);
    setIsMessageBarOpen(isOpen);

    if (type) {
      setAlertMessageType(type);
    }
  };

  const handleMessageBarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setIsMessageBarOpen(false);
    setAlertMessage("");
  };

  return (
    <React.Fragment>
      <Paper square>
        <AppBar position="static">
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabChange}
            aria-label="Two Tab Component"
            className={classes.tabs}
          >
            {/* Internal Engagements */}
            {isAccessGranted([
              Privileges.APPROVE_INTERNAL_ENGAGEMENTS_APPROVE,
              Privileges.APPROVE_INTERNAL_ENGAGEMENTS_VIEW,
              Privileges.APPROVE_INTERNAL_ENGAGEMENTS_EDIT,
            ]) && (
              <Tab
                label={
                  <Button className={classes.createNewButton} color="secondary">
                    Internal Engagements
                  </Button>
                }
                {...tabProps(tabIndexes.InternalEngagementsTab)}
                className={classes.tab}
              />
            )}

            {/* Marketing Events */}
            {isAccessGranted([
              Privileges.APPROVE_MARKETING_EVENTS_APPROVE,
              Privileges.APPROVE_MARKETING_EVENTS_VIEW,
              Privileges.APPROVE_MARKETING_EVENTS_EDIT,
            ]) && (
              <Tab
                label={
                  <Button className={classes.createNewButton} color="secondary">
                    Marketing Events
                  </Button>
                }
                {...tabProps(tabIndexes.marketingEventsTab)}
                className={classes.tab}
              />
            )}
          </Tabs>
        </AppBar>

        {/* Internal Engagements */}
        <TabPanel value={tab} index={0}>
          <div className={classes.page}>
            <Grid container spacing={3}>
              <ApproveEngagementView isInternalEngagement={true} />
              {setMessageBar}
            </Grid>
          </div>
        </TabPanel>

        {/* Marketing Events */}
        <TabPanel value={tab} index={1}>
          <div className={classes.page}>
            <Grid container spacing={3}>
              <ApproveEngagementView isInternalEngagement={false} />
            </Grid>
          </div>
        </TabPanel>

        {/* Snackbar for alerts */}
        <Portal>
          <Snackbar
            open={isMessageBarOpen}
            autoHideDuration={6000}
            onClose={handleMessageBarClose}
          >
            <Alert onClose={handleMessageBarClose} severity={alertMessageType}>
              {alertMessage}
            </Alert>
          </Snackbar>
        </Portal>
      </Paper>
    </React.Fragment>
  );
};

export default ApproveEngagements;
